import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { bulkMarkManualSoldOut, bulkUnmarkManualSoldOut } from 'services/scratcherService';

type ManualSoldOutPack = {
  game_id: string;
  pack_id: string;
};

type BulkManualSoldOutPayload = {
  packs: ManualSoldOutPack[];
  shouldUnmark: boolean;
};

type ManualSoldOutResult = {
  status?: string;
};

export function useBulkManualSoldOutMutation(storeId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BulkManualSoldOutPayload) => {
      if (!storeId) {
        throw new Error('Store ID is required');
      }

      const response = payload.shouldUnmark
        ? await bulkUnmarkManualSoldOut(storeId, payload.packs)
        : await bulkMarkManualSoldOut(storeId, payload.packs);

      return {
        response,
        shouldUnmark: payload.shouldUnmark,
        packs: payload.packs,
      };
    },
    onSuccess: async ({ response, shouldUnmark, packs }) => {
      if (!response.success) {
        toast.error(response.error || 'Bulk manual sold-out update failed');
      } else {
        const results = Array.isArray(response.data?.results)
          ? response.data.results as ManualSoldOutResult[]
          : [];
        const successStatus = shouldUnmark ? 'unmarked' : 'marked';
        const successCount = results.filter((result) => result.status === successStatus).length;
        const failedCount = results.length - successCount;

        if (successCount > 0) {
          toast.success(
            shouldUnmark
              ? `${successCount} pack${successCount === 1 ? '' : 's'} unmarked`
              : `${successCount} pack${successCount === 1 ? '' : 's'} marked Manual Sold Out`
          );
        }
        if (failedCount > 0) {
          toast.error(`${failedCount} pack${failedCount === 1 ? '' : 's'} could not be updated`);
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['current-inventory-status', storeId] }),
        queryClient.invalidateQueries({ queryKey: ['lottery-packs', storeId] }),
        queryClient.invalidateQueries({ queryKey: ['game-pack-report', storeId] }),
        ...packs.flatMap((pack) => [
          queryClient.invalidateQueries({ queryKey: ['game-history', storeId, pack.game_id] }),
          queryClient.invalidateQueries({ queryKey: ['pack-lifecycle', storeId, pack.game_id, pack.pack_id] }),
        ]),
      ]);
    },
  });
}
